/*
 * HomeCrest Layer 2 - Intent resolution
 * ===========================================================================
 * Turns a natural-language request into a structured design, WITHOUT limiting
 * what the customer is allowed to ask for.
 *
 *   request + capability -> resolver -> intent -> VALIDATOR -> validated
 *
 * THE OPEN-ENDED RULE
 *
 * There is no capability whitelist and no "this product does not offer that"
 * path. A customer may ask for anything: U-shaped, another door, a round top,
 * more premium, a complete redesign. Requests the resolver recognises become
 * structured modifications; everything else becomes a `structural` modification
 * carrying the normalised instruction. Nothing is refused for being unlisted.
 *
 * WHAT IS STILL VALIDATED
 *
 * Only claims about the REAL Shopify product: a variant selection must name an
 * option this product actually has, with a value that exists and is available.
 * That is not a customization limit - it is the difference between changing the
 * product you can buy today and describing a design HomeCrest will quote.
 *
 * The manufacturing boundary is deliberately NOT here. It sits after
 * visualisation, at submission, where a human reviews feasibility and price.
 *
 * NOTHING HERE TALKS TO AN EXTERNAL SERVICE. The shipped resolver is
 * deterministic and runs locally.
 *
 * This module renders nothing and touches no DOM belonging to the PDP.
 */

const INTENT_SCHEMA = 'homecrest.intent/3';

/* Dimension vocabulary. Customers name an axis by its adjective - "wide", not
   "width" - so the surface form has to resolve to a canonical axis. */
const DIMENSION_TERMS = {
  width: ['wide', 'width', 'across', 'wider', 'narrower'],
  depth: ['deep', 'depth', 'deeper'],
  height: ['tall', 'high', 'height', 'taller', 'shorter'],
  length: ['long', 'length', 'longer'],
};

const UNITS = {
  in: ['in', 'inch', 'inches', '"', '″'],
  cm: ['cm', 'centimetre', 'centimetres', 'centimeter', 'centimeters'],
  mm: ['mm', 'millimetre', 'millimetres', 'millimeter', 'millimeters'],
  m: ['m', 'metre', 'metres', 'meter', 'meters'],
  ft: ['ft', 'foot', 'feet'],
};

function canonicalUnit(raw) {
  const r = String(raw ?? '').toLowerCase().replace(/\.$/, '');
  for (const [unit, forms] of Object.entries(UNITS)) {
    if (forms.includes(r)) return unit;
  }
  return null;
}

/* Pulls every "<number> <unit>" pair out of a request. */
function extractMeasurements(text) {
  const out = [];
  const re = /(\d+(?:\.\d+)?)\s*(inches|inch|in|"|″|centimetres|centimeters|centimetre|centimeter|cm|millimetres|millimeters|mm|metres|meters|metre|meter|m|feet|foot|ft)\b/gi;
  let m;
  while ((m = re.exec(text)) !== null) {
    const unit = canonicalUnit(m[2]);
    if (unit) out.push({ value: Number(m[1]), unit, at: m.index });
  }
  return out;
}

/* ==========================================================================
   Resolver registry - deliberately provider-neutral
   ========================================================================== */

const resolvers = new Map();
let activeResolver = 'local';

function registerResolver(name, fn) {
  if (typeof fn !== 'function') throw new TypeError('resolver must be a function');
  resolvers.set(name, fn);
}

function setResolver(name) {
  if (!resolvers.has(name)) throw new Error(`unknown resolver: ${name}`);
  activeResolver = name;
}

function listResolvers() {
  return [...resolvers.keys()];
}

/* ==========================================================================
   Classification vocabulary
   --------------------------------------------------------------------------
   These decide which PRESERVATION PROFILE a request gets, not whether it is
   allowed. Every list below can fail to match and the request still proceeds,
   as a `structural` modification.
   ========================================================================== */

const TYPE_HINTS = [
  { type: 'addition', words: ['add', 'more', 'another', 'extra', 'additional', 'include', 'attach', 'insert', 'plus'] },
  { type: 'removal', words: ['remove', 'without', 'delete', 'drop', 'fewer', 'less', 'take', 'lose', 'omit', 'minus'] },
  { type: 'upholstery', words: ['fabric', 'upholstery', 'cover', 'covers', 'velvet', 'linen', 'leather', 'boucle', 'cushion', 'cushions'] },
  { type: 'finish', words: ['finish', 'stain', 'polish', 'lacquer', 'matt', 'matte', 'gloss', 'colour', 'color', 'shade', 'tone', 'painted'] },
  { type: 'material', words: ['material', 'timber', 'wood', 'species', 'oak', 'teak', 'sheesham', 'mango', 'acacia', 'marble', 'glass', 'metal', 'rattan', 'cane'] },
  { type: 'hardware', words: ['handle', 'handles', 'knob', 'knobs', 'pull', 'pulls', 'hinge', 'hinges', 'hardware', 'legs', 'leg', 'feet'] },
  { type: 'style', words: ['premium', 'luxurious', 'luxury', 'minimal', 'minimalist', 'modern', 'traditional', 'rustic', 'elegant', 'warmer', 'cooler', 'richer'] },
  /* Explicit structural cues. Without these, "U-shaped" contributes no family
     at all, so a compound request like "make it U-shaped and add a section"
     would look like a pure addition and the preservation list would then
     forbid the very reshaping the customer asked for. */
  { type: 'structural', words: [
    'shape', 'shaped', 'reshape', 'round', 'circular', 'oval', 'square', 'rectangular',
    'configuration', 'reconfigure', 'layout', 'sectional', 'chaise', 'corner', 'modular',
    'redesign', 'restructure', 'rebuild', 'combine', 'merge', 'split', 'separate',
  ] },
  /* Deliberately NOT cues: "section", "module", "unit". They are the nouns an
     ADDITION or REMOVAL request names ("add one more section"), so treating
     them as shape cues would push every such request to the broader structural
     profile and needlessly license changes the customer never asked for. */
];

/* Words that express a direction rather than a value. On a SELECTABLE axis they
   force a clarification instead of a guess. */
const COMPARATIVES = [
  'darker', 'lighter', 'dark', 'light', 'warmer', 'cooler', 'paler', 'richer',
];

const COMPARATIVE_AXES = {
  darker: ['finish', 'colour'], lighter: ['finish', 'colour'],
  dark: ['finish', 'colour'], light: ['finish', 'colour'],
  paler: ['finish', 'colour'], richer: ['finish', 'colour'],
  warmer: ['finish', 'colour'], cooler: ['finish', 'colour'],
};

const STOPWORDS = new Set([
  'make', 'it', 'a', 'an', 'the', 'in', 'with', 'and', 'or', 'to', 'me', 'i',
  'want', 'would', 'like', 'please', 'this', 'that', 'give', 'choose', 'get',
  'have', 'can', 'you', 'do', 'is', 'for', 'my', 'of', 'something', 'one',
  'could', 'should', 'also', 'but', 'so', 'be', 'its', 'im', 'id',
]);

const norm = (s) => String(s ?? '').toLowerCase().trim();
const tokens = (s) => norm(s).split(/[^a-z0-9]+/).filter((t) => t && !STOPWORDS.has(t));

/* ==========================================================================
   Instruction normalisation
   --------------------------------------------------------------------------
   The customer's sentence is tidied into a single-line instruction. It is NOT
   passed through verbatim and it is NOT the authority - Layer 4 sends this
   alongside a server-derived preservation list, and the Worker sanitises it
   again before it reaches any model.
   ========================================================================== */

function normaliseInstruction(text) {
  let s = String(text ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .replace(/^(please|can you|could you|i want|i would like|i'd like|id like)\s+/i, '')
    .replace(/^(to|and)\s+/i, '');

  if (!s) return '';
  s = s.charAt(0).toUpperCase() + s.slice(1);
  return s.replace(/[.!?]+$/, '');
}

/**
 * Chooses the PRESERVATION PROFILE for a request.
 *
 * Every matching family is collected rather than the first one winning. A
 * request that names two kinds of change gets `structural`, the superset
 * profile, because a narrower one would tell the model to preserve exactly the
 * attribute the customer asked to change:
 *
 *   "make it U-shaped and add one more section"
 *      first-match  -> addition  -> preserves silhouette + structure  (wrong)
 *      all-match    -> structural -> releases both                    (right)
 *
 * A single family keeps its precise profile, so "change the colour" still
 * releases only finish and colour, and "make it wider" only proportions.
 *
 * @returns {{type: string, families: string[]}}
 */
function classifyDetailed(words) {
  const families = new Set();

  for (const hint of TYPE_HINTS) {
    if (hint.words.some((w) => words.includes(w))) families.add(hint.type);
  }

  /* Dimension adjectives live in DIMENSION_TERMS rather than TYPE_HINTS,
     because the resolver also uses them to attach a measurement to an axis.
     They count as a family here so "make it wider" keeps the precise dimension
     profile instead of falling through to structural. */
  for (const terms of Object.values(DIMENSION_TERMS)) {
    if (terms.some((t) => words.includes(t))) {
      families.add('dimension');
      break;
    }
  }

  const list = [...families];
  if (list.length === 1) return { type: list[0], families: list };
  // Nothing recognised, or several families at once: the superset is the only
  // profile that cannot contradict part of the request.
  return { type: 'structural', families: list };
}

function classify(words) {
  return classifyDetailed(words).type;
}

/* ==========================================================================
   Deterministic default resolver - pass-through
   --------------------------------------------------------------------------
   Handles what needs no judgement (an exact variant value, a measurement with a
   named dimension) and passes EVERYTHING ELSE through as an open instruction
   rather than refusing it. It asks rather than guessing only where a guess
   would be invisible to the customer.
   ========================================================================== */

async function localResolver({ request, capability }) {
  const text = norm(request);
  const words = tokens(request);
  const selectable = capability?.selectable ?? [];

  const selections = [];
  const modifications = [];
  const clarifications = [];

  const instruction = normaliseInstruction(request);

  /* --- 1. an exact variant value named in full wins outright ----------- */
  const exactHits = [];
  for (const axis of selectable) {
    for (const v of axis.values) {
      if (text.includes(norm(v.value))) exactHits.push({ axis: axis.axis, value: v.value });
    }
  }
  const byAxis = new Map();
  for (const hit of exactHits) {
    const prev = byAxis.get(hit.axis);
    if (!prev || hit.value.length > prev.value.length) byAxis.set(hit.axis, hit);
  }
  for (const hit of byAxis.values()) selections.push(hit);
  const decided = new Set(selections.map((s) => s.axis));

  /* --- 2. a comparative on a real option asks rather than guesses ------ */
  for (const c of COMPARATIVES.filter((x) => words.includes(x))) {
    for (const name of COMPARATIVE_AXES[c] ?? []) {
      const axis = selectable.find((a) => a.axis === name);
      if (!axis || decided.has(axis.axis)) continue;
      if (clarifications.some((q) => q.axis === axis.axis)) continue;
      const options = axis.values.filter((v) => v.available).map((v) => v.value);
      if (options.length > 1) {
        clarifications.push({ axis: axis.axis, question: `Which ${axis.axis} would you prefer?`, options });
      }
    }
  }
  const asked = new Set(clarifications.map((q) => q.axis));

  /* --- 3. a distinctive single-token match on a real option ------------ */
  for (const axis of selectable) {
    if (decided.has(axis.axis) || asked.has(axis.axis)) continue;
    const matches = axis.values.filter((v) => tokens(v.value).some((vt) => words.includes(vt)));
    if (matches.length === 1) {
      selections.push({ axis: axis.axis, value: matches[0].value });
      decided.add(axis.axis);
    }
  }

  /* --- 4. measurements ------------------------------------------------- */
  const measurements = extractMeasurements(text);
  let measurementHandled = false;

  for (const measurement of measurements) {
    const named = Object.keys(DIMENSION_TERMS).find((axis) =>
      DIMENSION_TERMS[axis].some((term) => words.includes(term))
    );

    if (named) {
      modifications.push({
        type: 'dimension',
        axis: named,
        value: measurement.value,
        unit: measurement.unit,
        recognised: true,
        instruction: `Change overall ${named} to ${measurement.value} ${measurement.unit}`,
        source_text: String(request).trim(),
      });
      measurementHandled = true;
    } else if (measurements.length === 1 && !clarifications.length) {
      // A bare measurement is genuinely ambiguous. Asking is not a refusal.
      clarifications.push({
        axis: 'dimensions',
        question: `Which measurement should be ${measurement.value} ${measurement.unit}?`,
        options: ['width', 'height', 'depth'],
      });
      measurementHandled = true;
    }
  }

  /* --- 5. EVERYTHING ELSE becomes an open instruction ------------------
     This is the open-ended rule. If the request said something that was not
     fully absorbed by a variant selection, a measurement, or a pending
     question, it becomes a modification. It is never discarded and never
     refused. */
  const detail = classifyDetailed(words);
  const absorbed = selections.length > 0 || measurementHandled || clarifications.length > 0;
  const onlyVariantWords = selections.length > 0 && words.every((w) =>
    selections.some((s) => tokens(s.value).includes(w)) || DIMENSION_TERMS.width.includes(w)
  );

  if (instruction && (!absorbed || (!onlyVariantWords && !measurementHandled && !clarifications.length))) {
    modifications.push({
      type: detail.type,
      families: detail.families,
      axis: null,
      value: null,
      unit: null,
      recognised: false,
      instruction,
      source_text: String(request).trim(),
    });
  }

  let confidence = 'medium';
  if (selections.length && !modifications.length && !clarifications.length) confidence = 'high';
  else if (clarifications.length) confidence = 'low';

  return {
    schema: INTENT_SCHEMA,
    selections,
    modifications,
    needs_clarification: clarifications,
    confidence,
  };
}

registerResolver('local', localResolver);

/* ==========================================================================
   Validator
   --------------------------------------------------------------------------
   Validates ONLY claims about the real Shopify product. It has no opinion about
   whether a modification is offered, because that is no longer a question this
   layer is allowed to ask.
   ========================================================================== */

function validateIntent(intent, capability) {
  const errors = [];
  const selectable = capability?.selectable ?? [];
  const axisByName = new Map(selectable.map((a) => [a.axis, a]));

  if (!intent || typeof intent !== 'object') {
    return { ok: false, errors: [{ code: 'malformed', message: 'No intent object.' }], validated: null };
  }
  if (intent.schema !== INTENT_SCHEMA) {
    errors.push({ code: 'bad_schema', message: `Expected ${INTENT_SCHEMA}, got ${intent.schema}` });
  }

  const selections = Array.isArray(intent.selections) ? intent.selections : [];
  const seen = new Map();

  for (const sel of selections) {
    const axis = axisByName.get(sel?.axis);

    /* An axis this product does not have is not an error any more - it simply
       is not a variant selection. It is dropped here and the request survives
       as a modification, so nothing the customer said is lost. */
    if (!axis) continue;

    const allowed = axis.values.find((v) => v.value === sel.value);
    if (!allowed) continue;

    // Availability IS enforced: we must not select a variant that cannot be sold.
    if (allowed.available !== true) {
      errors.push({
        code: 'unavailable_value', axis: sel.axis, value: sel.value,
        message: `"${sel.value}" is not currently available.`,
      });
      continue;
    }

    if (seen.has(sel.axis) && seen.get(sel.axis) !== sel.value) {
      errors.push({
        code: 'conflicting_selection', axis: sel.axis,
        message: `Two different ${sel.axis} values were selected.`,
      });
      continue;
    }
    seen.set(sel.axis, sel.value);
  }

  /* Modifications are shape-checked, never capability-checked. */
  const modifications = (Array.isArray(intent.modifications) ? intent.modifications : [])
    .filter((m) => m && (m.instruction || m.value != null))
    .map((m) => ({
      type: typeof m.type === 'string' ? m.type : 'structural',
      axis: m.axis ?? null,
      value: m.value ?? null,
      unit: m.unit ?? null,
      recognised: m.recognised === true,
      families: Array.isArray(m.families) ? m.families.filter((f) => typeof f === 'string') : [],
      instruction: String(m.instruction ?? '').slice(0, 300),
      source_text: String(m.source_text ?? '').slice(0, 300),
    }));

  if (errors.length) {
    return { ok: false, errors, validated: null };
  }

  return {
    ok: true,
    errors: [],
    validated: {
      schema: INTENT_SCHEMA,
      selections: [...seen].map(([axis, value]) => ({ axis, value })),
      modifications,
      needs_clarification: intent.needs_clarification ?? [],
      confidence: intent.confidence ?? 'medium',
    },
  };
}

/* ==========================================================================
   Pipeline
   ========================================================================== */

function readCapability(root = document) {
  const el = root.querySelector('[data-homecrest-capability]');
  if (!el) return null;
  try {
    return JSON.parse(el.textContent);
  } catch {
    return null;
  }
}

/** request -> resolver -> validator. Applies NOTHING; Layer 3 owns application. */
async function resolve({ request, capability = readCapability(), resolver = activeResolver } = {}) {
  if (!capability) return { ok: false, errors: [{ code: 'no_capability', message: 'No capability object.' }], validated: null };

  const fn = resolvers.get(resolver);
  if (!fn) return { ok: false, errors: [{ code: 'no_resolver', message: `Unknown resolver: ${resolver}` }], validated: null };

  let intent;
  try {
    intent = await fn({ request, capability });
  } catch (error) {
    return { ok: false, errors: [{ code: 'resolver_failed', message: String(error?.message ?? error) }], validated: null };
  }

  const result = validateIntent(intent, capability);
  return { ...result, raw: intent };
}

window.HomeCrestIntent = {
  SCHEMA: INTENT_SCHEMA,
  DIMENSION_TERMS,
  registerResolver,
  setResolver,
  listResolvers,
  readCapability,
  resolve,
  validateIntent,
  normaliseInstruction,
  classify,
  classifyDetailed,
  extractMeasurements,
};
